import { Component, computed, input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { DynamicComponentRenderer } from 'src/app/@shared/directives/dynamic-component-renderer.directive';
import { IToolCall } from 'src/app/features/general-agent/models/tool-call.model';
import { TOOLS } from 'src/app/features/general-agent/constants/tool.constants';
import { MarkdownRenderPipe } from 'src/app/@shared/pipes/markdown-render.pipe';

@Component({
    selector: 'app-file-operation-tool-view',
    standalone: true,
    imports: [CommonModule, MarkdownRenderPipe, DynamicComponentRenderer],
    templateUrl: './file-operation-tool-view.component.html',
    styleUrl: './file-operation-tool-view.component.scss'
})
export class FileOperationToolViewComponent {
    //#region Inputs
    toolCall = input<Partial<IToolCall>>({});
    //#endregion

    //#region Computed values
    toolName = computed(() => {
        return this.toolCall()?.name as TOOLS;
    });

    filePath = computed(() => {
        const args = this.toolCall()?.args;
        return args && 'file_path' in args ? args.file_path : '';
    });

    toolContent = computed(() => {
        const toolCall = this.toolCall();

        // ToolCallPanelService has already processed and set toolContent
        return toolCall?.toolContent || '';
    });

    isReadFile = computed(() => {
        const name = this.toolCall()?.name as string;
        return name === TOOLS.READ_FILE || name === 'read_file';
    });

    isListFiles = computed(() => {
        const name = this.toolCall()?.name as string;
        return name === TOOLS.LIST_FILES || name === 'ls';
    });

    // Input text to display
    inputText = computed(() => {
        const name = this.toolCall()?.name as string;
        const filePath = this.filePath();

        if (name === TOOLS.READ_FILE || name === 'read_file') {
            return `Read file: ${filePath || 'unknown'}`;
        } else if (name === TOOLS.LIST_FILES || name === 'ls') {
            return 'List all files';
        }
        return '';
    });

    // For read_file: strip line numbers and preserve special formatting
    strippedContent = computed(() => {
        if (!this.isReadFile()) return '';

        const content = this.toolContent();
        if (!content) return '';


        // Strip line numbers from content like "     1\tIndian National Animal: Tiger"
        // Also escape lines that are only dashes/equals to prevent markdown interpretation
        return content
            .split('\n')
            .map(line => {
                // Remove line numbers
                const strippedLine = line.replace(/^\s*\d+\t/, '');

                // Check if line is only made of dashes, equals, or underscores (markdown horizontal rules)
                // Escape by adding a backslash before first character (markdown escape that won't show)
                if (/^[\s]*[-=_]{3,}[\s]*$/.test(strippedLine)) {
                    const trimmed = strippedLine.trim();
                    return '\\' + trimmed;
                }

                return strippedLine;
            })
            .join('\n');
    });

    // For ls: parse and format file list simply
    parsedFileList = computed(() => {
        if (!this.isListFiles()) return '';

        const content = this.toolContent();
        if (!content) return '';

        try {
            // Try parsing as JSON array first
            const parsed = JSON.parse(content);
            if (Array.isArray(parsed)) {
                return parsed.join('\n');
            }
        } catch {
            // If not JSON, return as-is
            return content;
        }

        return content;
    });
    //#endregion
}
